import { useEffect, useState } from "react";
import { api } from "../../api/client";
import css from "./AuditHistory.module.css";

/**
 * Collapsible «◇ ИСТОРИЯ (N)» section per B.3b.3. Sits under the comment
 * textarea, before the sticky footer. Renders nothing if there's no
 * booking ID (i.e. create-state).
 *
 * Action colour mapping (per spec):
 *   - created_*   → green     "Создана бронь / лид"
 *   - prepay_*    → gold      "Задаток"
 *   - updated_*   → orange    "Изменение"
 *   - moved_*     → cyan      "Перенос"
 *   - cancelled_* → red       "Отмена"
 *
 * Data: GET /api/bookings/full?id=N → response.audit. Lazy-loaded only when
 * the section is first expanded (collapsed by default).
 */
export interface AuditHistoryProps {
  bookingId: number;
}

interface AuditRow {
  id: number;
  staff_id?: string | null;
  staff_name?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  action: string;
  detail: string | null;
  meta?: string | null;
  created_at: string;
  ip?: string | null;
}

interface ChangeEntry {
  field: string;
  label: string;
  short: string;
  from?: unknown;
  to?: unknown;
}

/** Russian labels for stand-alone meta keys (when meta is a flat object,
   not `{changes:[]}`). Lets us render `accept_discrepancy` rows as
   «Получено: 16000 ₽ · Ожидалось: 17000 ₽ · Разница: −1000 ₽». */
const META_KEY_LABEL: Record<string, string> = {
  diff: "Разница",
  received: "Получено",
  expected: "Ожидалось",
  amount: "Сумма",
  reason: "Причина",
  operator_id: "Оператор",
  rate_paid: "Ставка",
  oldQuestName: "Откуда",
  oldDate: "Дата (было)",
  oldTime: "Время (было)",
};

function parseChanges(metaRaw: string | null | undefined): ChangeEntry[] {
  if (!metaRaw) return [];
  let m: unknown;
  try { m = JSON.parse(metaRaw); } catch { return []; }
  if (!m || typeof m !== "object") return [];
  /* Preferred shape: { changes: [{field, label, short, from, to}, ...] } */
  const obj = m as Record<string, unknown>;
  if (Array.isArray(obj.changes)) return obj.changes as ChangeEntry[];
  /* Fallback: flat object of meta keys — render each as a single ChangeEntry
     so the UI shows them. Skip noisy keys like `booking_id` (we know it). */
  const skip = new Set(["booking_id", "id", "entity_id"]);
  const entries: ChangeEntry[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (skip.has(k) || v == null) continue;
    const label = META_KEY_LABEL[k] ?? k;
    entries.push({
      field: k,
      label,
      short: `${label}: ${String(v).slice(0, 32)}`,
      to: v,
    });
  }
  return entries;
}

type ActionTone = "green" | "gold" | "orange" | "cyan" | "red" | "neutral";

interface ActionMeta {
  tone: ActionTone;
  label: string;
}

/* Full prefix→{tone,label} map keyed by entity_type. Falls through to
   ANY_ENTITY for prefixes that apply across entity types. */
const TONE_BY_ENTITY: Record<string, Record<string, ActionMeta>> = {
  booking: {
    created_booking:    { tone: "green",  label: "Создана бронь" },
    created_lead:       { tone: "green",  label: "Создан лид" },
    created_block:      { tone: "neutral", label: "Создан блок" },
    moved_booking:      { tone: "cyan",   label: "Перенос" }, displaced_booking: { tone: "neutral", label: "Уступила слот (отмена/архив)" },
    cancelled_booking:  { tone: "red",    label: "Отмена" },
    deleted_lead:       { tone: "red",    label: "Удалён лид" },
    archived_booking:   { tone: "red",    label: "Архив" },
    restored_booking:   { tone: "orange", label: "Восстановление" },
    prepay_paid:        { tone: "gold",   label: "Задаток" },
    prepay_refund:      { tone: "gold",   label: "Возврат задатка" },
    updated_booking:    { tone: "orange", label: "Изменение" },
    updated_lead:       { tone: "orange", label: "Изменение лида" },
  },
  assignment: {
    created_assignment: { tone: "cyan",   label: "Назначен оператор" },
    deleted_assignment: { tone: "red",    label: "Снят оператор" },
    updated_assignment: { tone: "orange", label: "Замена оператора" },
    publish_assignments: { tone: "green", label: "Расписание опубликовано" },
  },
  report: {
    created_report:     { tone: "green",  label: "Игра проведена" },
    accept_report:      { tone: "green",  label: "Отчёт принят" },
    accept_discrepancy: { tone: "gold",   label: "Несоответствие принято" },
    updated_report:     { tone: "orange", label: "Отчёт изменён" },
    deleted_report:     { tone: "red",    label: "Отчёт удалён" },
  },
  tea: {
    tea_cleanup_assigned: { tone: "cyan",   label: "Назначена уборка чайной" },
    tea_cleanup_confirmed:{ tone: "green",  label: "Уборка чайной подтверждена" },
  },
  mc: {
    mc_status_operator:   { tone: "cyan",   label: "Статус МК (оператор)" },
    mc_status_completed:  { tone: "green",  label: "МК проведён" },
  },
};

function actionTone(action: string, entityType?: string | null): ActionMeta {
  if (entityType && TONE_BY_ENTITY[entityType]) {
    const exact = TONE_BY_ENTITY[entityType][action];
    if (exact) return exact;
  }
  /* Fallback: scan booking-flavoured prefixes. */
  for (const [key, meta] of Object.entries(TONE_BY_ENTITY.booking || {})) {
    if (action.startsWith(key)) return meta;
  }
  return { tone: "neutral", label: action };
}

function fmtTime(iso: string): string {
  /* "2026-05-15 13:24:01" → "15.05 13:24" */
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}.${m[2]} ${m[4]}:${m[5]}`;
}

export function AuditHistory({ bookingId }: AuditHistoryProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string>("");

  /* Lazy load on first expand. */
  useEffect(() => {
    if (!open || rows !== null || loading) return;
    setLoading(true);
    setError("");
    api<{ audit?: AuditRow[] }>(`/api/bookings/full?id=${bookingId}`)
      .then((res) => {
        setRows(res.audit ?? []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "Ошибка загрузки");
        setRows([]);
        setLoading(false);
      });
  }, [open, rows, loading, bookingId]);

  const count = rows?.length ?? 0;

  return (
    <div className={css.section}>
      <button
        type="button"
        className={css.header}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={css.chev} aria-hidden="true">{open ? "▾" : "▸"}</span>
        <span className={css.label}>ИСТОРИЯ</span>
        {rows !== null ? <span className={css.count}>({count})</span> : null}
      </button>

      {open ? (
        <div className={css.body}>
          {loading ? (
            <div className={css.empty}>Загрузка…</div>
          ) : error ? (
            <div className={css.errorRow}>{error}</div>
          ) : count === 0 ? (
            <div className={css.empty}>Нет событий</div>
          ) : (
            <ol className={css.list}>
              {rows!.map((r) => {
                const { tone, label } = actionTone(r.action, r.entity_type);
                const changes = parseChanges(r.meta);
                return (
                  <li key={r.id} className={css.row}>
                    <span className={css.time}>{fmtTime(r.created_at)}</span>
                    <div className={css.body}>
                      <div className={css.head}>
                        <span className={`${css.action} ${css[`tone_${tone}`]}`}>{label}</span>
                        <span className={css.who}>{r.staff_name ?? "—"}</span>
                      </div>
                    {changes.length > 0 ? (
                      <ul className={css.changes}>
                        {changes.map((c, i) => {
                          const hasDiff = c.from !== undefined && c.to !== undefined;
                          const hasValueOnly = c.to !== undefined && c.from === undefined;
                          return (
                            <li key={c.field + i} className={css.changeItem}>
                              <span className={css.changeLabel}>{c.label}:</span>{" "}
                              {hasDiff ? (
                                <>
                                  <span className={css.changeFrom}>{String(c.from ?? "—")}</span>
                                  <span className={css.changeArrow}>→</span>
                                  <span className={css.changeTo}>{String(c.to ?? "—")}</span>
                                </>
                              ) : hasValueOnly ? (
                                <span className={css.changeTo}>{String(c.to)}</span>
                              ) : (
                                <span className={css.changePii}>изменено</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : r.detail ? (
                      <span className={css.detail}>{r.detail}</span>
                    ) : null}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      ) : null}
    </div>
  );
}
